import React from 'react';
import classnames from 'classnames';
import { makeStyles } from 'design-system/styles';
import { SelectField, FieldSet } from '@scale/shared/types/params/textcollection';
import {
  SelectFieldAnnotation,
  isFieldSetAnnotation,
  TextCollectionUnitValueType,
  FieldSetAnnotation,
  isUnitFieldAnnotation,
} from '@scale/shared/types/response/textcollection';
import { Select, Option } from 'design-system/Select';
import { useAnnotationLookup } from './document';
import { getFieldSetTitle } from './fieldSetUtils';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { FormHelperText } from '@material-ui/core';
import ReactTooltip from 'react-tooltip';
import Markdown from '@scale/frontend-lib/markdown';
import FieldHeader from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/FieldHeader';

interface SelectFieldSectionProps {
  field: SelectField;
  annotation: SelectFieldAnnotation;
  setAnnotation: (annotation: SelectFieldAnnotation) => void;
  disabled?: boolean;
  errorMessage?: string;
}

interface SelectChoice {
  value: string;
  label: string;
}

export default function SelectFieldSection({
  field,
  annotation,
  setAnnotation,
  disabled,
}: SelectFieldSectionProps) {
  const { lookupAnnotation } = useAnnotationLookup();
  const onChange = function (index: number, value: string): void {
    const response = annotation.response.slice();
    response[index] = value;
    const newAnnotation: SelectFieldAnnotation = {
      ...annotation,
      response,
    };
    setAnnotation(newAnnotation);
  };
  const classes = useStyles();
  const choices: SelectChoice[] = React.useMemo(() => {
    if (field.choices) {
      return field.choices.map(value => ({
        value,
        label: value,
      }));
    } else if (field.choices_from_field) {
      const result = lookupAnnotation(field.choices_from_field);
      if (!result) {
        return [];
      }
      if (isFieldSetAnnotation(result.annotation)) {
        return (
          result.annotation.response.map((annotations, index) => ({
            label: getFieldSetTitle(result.field as FieldSet, annotations),
            value: (result.annotation as FieldSetAnnotation).uuids?.[index] || '',
          })) || []
        );
      } else if (isUnitFieldAnnotation(result.annotation)) {
        const responses: TextCollectionUnitValueType[] = result.annotation.response;
        return responses
          .map(r => r.toString())
          .map(value => ({
            value,
            label: value,
          }));
      }
    }
    return [];
  }, [field, lookupAnnotation]);
  const dropdowns = annotation.response.map((response: string, index: number) => {
    const isRequiredAndMissing = !response && !!field.required;
    return (
      <div key={`select-${index}`}>
        <FieldHeader title={field.title} description={field.description} hint={field.hint} />
        <div className={classes.selectContainer}>
          <Select
            value={response}
            onChange={e => onChange(index, e.target.value as string)}
            disabled={disabled}
            classes={{
              root: classnames(classes.select, {
                [classes.error]: isRequiredAndMissing,
              }),
            }}
            columns={Math.min(field.columns ?? 1, choices.length)}
          >
            {choices.map((choice, j) => (
              <Option key={`select-option-${index}-${j}`} value={choice.value}>
                {choice.label}
              </Option>
            ))}
          </Select>
          {field.hint && (
            <>
              <div
                className={classes.tooltipInfoIcon}
                data-tip
                data-for={`hint-tooltip-${field.field_id}-${index}`}
              >
                <InfoIcon />
              </div>
              <ReactTooltip id={`hint-tooltip-${field.field_id}-${index}`} place="top">
                <div
                  className={classes.tooltipText}
                  dangerouslySetInnerHTML={{ __html: Markdown.renderInline(field.hint) }}
                />
              </ReactTooltip>
            </>
          )}
        </div>
        {isRequiredAndMissing ? (
          <FormHelperText classes={{ root: classes.missingHelperText }}>
            Missing. Please select a value.
          </FormHelperText>
        ) : null}
      </div>
    );
  });
  return <div>{dropdowns}</div>;
}

const useStyles = makeStyles({
  title: {
    color: '#000000',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    marginBottom: '8px',
  },
  valueDisplay: {
    fontSize: '13px',
  },
  selectContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 8,
    marginLeft: 8,
    flexGrow: 1,
  },
  select: {
    minWidth: 128,
  },
  fieldSetHelperText: {
    marginBottom: '16px',
  },
  error: {
    background: 'rgba(255, 127, 127, 0.25) !important',
  },
  missingHelperText: {
    color: '#EA5F28',
  },
  tooltipInfoIcon: {
    paddingLeft: 8,
    paddingRight: 8,
    color: 'rgba(1, 1, 1, 0.75)',
  },
  tooltipText: {
    maxWidth: 496,
  },
});

const InfoIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M6.9998 0.799805C3.57588 0.799805 0.799805 3.57688 0.799805 6.9998C0.799805 10.4247 3.57588 13.1998 6.9998 13.1998C10.4237 13.1998 13.1998 10.4247 13.1998 6.9998C13.1998 3.57688 10.4237 0.799805 6.9998 0.799805ZM6.9998 3.5498C7.5797 3.5498 8.0498 4.0199 8.0498 4.5998C8.0498 5.1797 7.5797 5.6498 6.9998 5.6498C6.4199 5.6498 5.9498 5.1797 5.9498 4.5998C5.9498 4.0199 6.4199 3.5498 6.9998 3.5498ZM8.3998 9.8998C8.3998 10.0655 8.26548 10.1998 8.0998 10.1998H5.8998C5.73413 10.1998 5.5998 10.0655 5.5998 9.8998V9.2998C5.5998 9.13413 5.73413 8.9998 5.8998 8.9998H6.1998V7.3998H5.8998C5.73413 7.3998 5.5998 7.26548 5.5998 7.0998V6.4998C5.5998 6.33413 5.73413 6.1998 5.8998 6.1998H7.4998C7.66548 6.1998 7.7998 6.33413 7.7998 6.4998V8.9998H8.0998C8.26548 8.9998 8.3998 9.13413 8.3998 9.2998V9.8998Z"
    />
  </svg>
);
